import { Avatar, FOCUS_RING_NO_RADIUS, Icon, type IconName } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { EmployeeWithoutObjective } from '@/types/objectives';

import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface TileCaptionProps {
    iconName: IconName;
    text: string;
}

/** A glyph and one line of secondary copy, clipped to the width the tile leaves it. */
function TileCaption({ iconName, text }: TileCaptionProps) {
    return (
        <span className={'text-obj-slate flex min-w-0 items-center gap-1 text-[11px]'}>
            <span className={'flex flex-shrink-0'}>
                <Icon
                    name={iconName}
                    size={'xs'}
                    color={'inherit'}
                    ariaHidden
                />
            </span>

            {/* The ellipsis belongs to the text, not to the row: a `truncate` on the flex
                container above would clip the glyph instead. */}
            <span className={'truncate'}>{text}</span>
        </span>
    );
}

interface EmployeeWithoutObjectiveTileProps {
    employee: EmployeeWithoutObjective;
    /** Opens the objective form for this employee; the whole tile triggers it. */
    onSelect: () => void;
}

/**
 * One employee with no objective. The tile is a single `button`: it carries its own surface, so
 * a click anywhere on it -- padding and border included -- opens the objective form for that
 * person, and the keyboard reaches it like any other button.
 */
export default function EmployeeWithoutObjectiveTile({
    employee,
    onSelect,
}: EmployeeWithoutObjectiveTileProps) {
    const { t } = useTranslation();
    // The API sends the job title and the department as ids; their labels live in the company
    // information, the same way every other list in the app resolves them.
    const { getJobTitle, getDepartment, getManagerLabel } = useCompanyInformation();
    const fullName = getFullName(employee);
    const jobTitle = getJobTitle(employee.jobTitleId ?? '');
    const department = getDepartment(employee.departmentId ?? '');
    const managerName = employee.manager ? getFullName(employee.manager) : null;
    const managerLabel = getManagerLabel();

    return (
        <button
            type={'button'}
            onClick={onSelect}
            aria-label={t('employeesWithoutObjectives.addObjectiveFor', { name: fullName })}
            className={`${FOCUS_RING_NO_RADIUS} flex w-full flex-col gap-3 rounded-[12px] border border-white bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md`}
            data-testid={`employee-without-objective-tile-${employee.id}`}
        >
            <span className={'flex items-center gap-4'}>
                <Avatar
                    src={getProfilePictureUrl(employee.profilePictureUrl)}
                    alt={fullName}
                    initials={getInitials(employee)}
                    size={'sm'}
                    bgColor={'gradient'}
                />

                {/* The identity takes the whole tile: the action sits on its own row below, so
                    nothing squeezes the job title, the department or the manager. */}
                <span className={'flex min-w-0 flex-1 flex-col gap-0.5'}>
                    <span className={'text-navy truncate text-[14px] font-medium'}>{fullName}</span>

                    {jobTitle && (
                        <span className={'text-obj-slate truncate text-[12px]'}>{jobTitle}</span>
                    )}

                    {department && (
                        <TileCaption
                            iconName={'building-buildings'}
                            text={department}
                        />
                    )}

                    <TileCaption
                        iconName={'users-profile'}
                        text={
                            managerName ??
                            t('employeesWithoutObjectives.noManager', { managerLabel })
                        }
                    />
                </span>
            </span>

            {/* `mt-auto` keeps the rule on the tile's floor: the job title and the department are
                conditional, so neighbours in a stretched row would otherwise misalign. */}
            <span
                className={
                    'border-obj-line text-obj-accent mt-auto flex items-center justify-end gap-1 border-t pt-3 text-[12px] font-medium'
                }
            >
                <Icon
                    name={'essential-add'}
                    size={'sm'}
                    color={'inherit'}
                    ariaHidden
                />

                {t('employeesWithoutObjectives.addObjective')}
            </span>
        </button>
    );
}
